import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { AudiencePicker } from './AudiencePicker';
import { PLACEHOLDER_HINT } from './EmailCampaignDialog';
import { defaultEmail } from '@/lib/buyerMessaging';

const NONE = '__none__';
const cls = 'bg-white border-gray-300';

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs text-gray-500">{label}</Label>{children}</div>;
}

export function NewEmailCampaignDialog({ open, onOpenChange, onSent }) {
  const [selected, setSelected] = useState(new Set());
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [leadId, setLeadId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const d = defaultEmail(null);
    setSubject(d.subject); setBody(d.body); setLeadId('');
    API.get('/leads').then(r => setLeads(r.data)).catch(() => {});
    API.get('/email-templates').then(r => setTemplates(r.data)).catch(() => {});
  }, [open]);

  const pickLead = (v) => {
    const id = v === NONE ? '' : v;
    setLeadId(id);
    if (id && subject === defaultEmail(null).subject) { const d = defaultEmail({}); setSubject(d.subject); setBody(d.body); }
  };

  const pickTemplate = (id) => {
    const t = templates.find(x => x.id === id);
    if (t) { setSubject(t.subject); setBody(t.html_content); }
  };

  const send = async () => {
    if (!selected.size) { toast.error('Pick at least one recipient'); return; }
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message are required'); return; }
    setSending(true);
    try {
      const res = await API.post('/buyer-intel/email-campaign', { buyer_ids: [...selected], subject, html_content: body, lead_id: leadId || null });
      if (res.data.sent_count > 0) toast.success(res.data.message); else toast.error(res.data.message);
      onOpenChange(false); onSent();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-email-campaign-dialog">
        <DialogHeader><DialogTitle>New Email Campaign</DialogTitle></DialogHeader>
        <Field label="Recipients">{open && <AudiencePicker selected={selected} setSelected={setSelected}/>}</Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Property (optional)">
            <Select value={leadId || NONE} onValueChange={pickLead}>
              <SelectTrigger data-testid="campaign-property-select" className={cls}><SelectValue placeholder="No property" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64"><SelectItem value={NONE}>No property</SelectItem>{leads.map(l => <SelectItem key={l.id} value={l.id}>{l.property_address || l.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Template (optional)">
            <Select onValueChange={pickTemplate}>
              <SelectTrigger data-testid="campaign-template-select" className={cls}><SelectValue placeholder={templates.length ? 'Load a template' : 'No saved templates'} /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Subject"><Input data-testid="new-campaign-subject" value={subject} onChange={e => setSubject(e.target.value)} className={cls} /></Field>
        <Field label="Message">
          <Textarea data-testid="new-campaign-body" value={body} onChange={e => setBody(e.target.value)} rows={8} className={`${cls} font-mono text-xs`} />
          <p className="text-[11px] text-gray-400">Placeholders: {PLACEHOLDER_HINT}{!leadId && ' (property ones need a property selected)'}</p>
        </Field>
        <Button data-testid="new-campaign-send" onClick={send} disabled={sending || !selected.size} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white">
          <Send className="h-4 w-4 mr-2" /> {sending ? 'Sending...' : `Send to ${selected.size} buyer${selected.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
